import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { listMyJobs } from '../../api/jobs'
import { ApiError } from '../../api/problem'
import type { BackgroundJobView } from '../../types/job'
import { usePolling } from './usePolling'

const ACTIVE_STATUSES = new Set(['queued', 'running'])
const PAGE_LIMIT = 25
const OPEN_INTERVAL_MS = 5_000
const CLOSED_ACTIVE_INTERVAL_MS = 20_000

export type MyJobsState = 'loading' | 'error' | 'ready'

export interface UseMyJobsResult {
  /** `queued`/`running`, most-recent-first (server order, unchanged). */
  active: BackgroundJobView[]
  /** Terminal statuses, most-recent-first (server order, unchanged). */
  recent: BackgroundJobView[]
  /** `null` only when NO fetch has ever succeeded (AC2 failure path: the badge is absent, never a
   * stale/zero fake); otherwise the count of `active` rows in the last successful fetch. */
  runningCount: number | null
  state: MyJobsState
  errorMessage: string | null
  /** Manual pull (the tray's refresh button, and the first-load error state's Retry). */
  refetch: () => void
  /** Applied after a cancel's `202` response: replaces the row in place (or inserts it) from the
   * server's OWN returned view — never an optimistic client-guessed transition (AC6). */
  applyJobUpdate: (updated: BackgroundJobView) => void
}

function partition(items: BackgroundJobView[]): { active: BackgroundJobView[]; recent: BackgroundJobView[] } {
  const active: BackgroundJobView[] = []
  const recent: BackgroundJobView[] = []
  for (const job of items) {
    ;(ACTIVE_STATUSES.has(job.status) ? active : recent).push(job)
  }
  return { active, recent }
}

function toErrorMessage(err: unknown): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load your background jobs.'
}

/**
 * Owns fetch + poll + partition + badge for the personal Jobs tray (FR-M23.11, design §4.2/§4.3).
 * `open` gates the polling cadence:
 *  - 5s while the panel is open.
 *  - ~20s while closed IF the last successful fetch showed at least one active (queued/running)
 *    job (cross-tab/cross-session visibility without a wasteful cadence).
 *  - otherwise idle — no interval at all; a lazy refresh happens on the next panel open or window
 *    focus (an idle user with no running work generates ~no background traffic).
 *
 * A poll failure AFTER the first successful load never flashes an error or clears prior rows/badge
 * (AC4 failure path) — only a FIRST-load failure surfaces the error state.
 */
export function useMyJobs(open: boolean): UseMyJobsResult {
  const [items, setItems] = useState<BackgroundJobView[] | null>(null)
  const [state, setState] = useState<MyJobsState>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const hasLoadedOnce = useRef(false)
  /** Monotonic request sequence (design gap fix): the mount fetch, the "fresh pull on open" fetch,
   * a poll tick, and a window-focus refresh can all be issued in overlapping quick succession —
   * network responses can then resolve OUT OF ISSUE ORDER (a slower earlier request completing
   * after a faster later one), which is last-WINS, not last-ISSUED, if applied naively. Each call
   * stamps its own sequence number at ISSUE time; a response is only applied if it's still the
   * most-recently-issued request when it resolves — a stale/slow response is silently ignored
   * rather than reverting the UI to older data (e.g. a completed row flashing back to "running"). */
  const requestSeqRef = useRef(0)

  const fetchJobs = useCallback(() => {
    const seq = ++requestSeqRef.current
    listMyJobs({ limit: PAGE_LIMIT })
      .then((page) => {
        if (seq !== requestSeqRef.current) return // superseded by a later-issued fetch — ignore.
        hasLoadedOnce.current = true
        setItems(page.items)
        setState('ready')
        setErrorMessage(null)
      })
      .catch((err) => {
        if (seq !== requestSeqRef.current) return // superseded by a later-issued fetch — ignore.
        if (!hasLoadedOnce.current) {
          setState('error')
          setErrorMessage(toErrorMessage(err))
        }
        // else: a transient poll failure — keep the last-known items/state, retry next tick/open.
      })
  }, [])

  // First load, once.
  useEffect(() => {
    fetchJobs()
  }, [fetchJobs])

  // A fresh pull the instant the tray opens (on top of the 5s cadence that starts below).
  useEffect(() => {
    if (open) fetchJobs()
  }, [open, fetchJobs])

  // While closed, a returning tab still gets one fresh read even in the "lazy idle" cadence.
  useEffect(() => {
    function onFocus() {
      if (!open) fetchJobs()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [open, fetchJobs])

  const { active, recent } = useMemo(() => partition(items ?? []), [items])
  const runningCount = items === null ? null : active.length
  const lazyIdleClosed = !open && (runningCount ?? 0) === 0

  usePolling(fetchJobs, {
    intervalMs: open ? OPEN_INTERVAL_MS : CLOSED_ACTIVE_INTERVAL_MS,
    enabled: !lazyIdleClosed,
  })

  const applyJobUpdate = useCallback((updated: BackgroundJobView) => {
    setItems((prev) => {
      if (prev === null) return [updated]
      const idx = prev.findIndex((j) => j.id === updated.id)
      if (idx === -1) return [updated, ...prev]
      const next = [...prev]
      next[idx] = updated
      return next
    })
  }, [])

  return { active, recent, runningCount, state, errorMessage, refetch: fetchJobs, applyJobUpdate }
}
